import { useEffect, useRef, useState } from 'react';
import { Bell, ChevronRight } from 'lucide-react';
import type { PendingAction } from '../../state/usePendingActions';
import type { View } from '../../app/views/types';
import { useT } from '../../i18n';

// ═══════════════════════════════════════════════════════════════════
// THE BELL — «what needs me» for whoever runs the organization (owner,
// 2026-10-20). Sits beside «Meldinger» in the portal header.
//
// ── ABSENT, NOT EMPTY, FOR SOMEBODY IT IS NOT FOR ─────────────────
//
// `actions` holds only the kinds the server let this person read. Empty means
// nothing is theirs (or nothing answered yet), and then there is no bell at
// all — the same rule as the organization switcher: a control that opens
// onto nothing is worse than no control. A kind with count 0 is different:
// that IS theirs, the bell draws without a badge, and the menu says nothing
// waits.
//
// ── NAVIGATING THERE IS THE ACTION ────────────────────────────────
//
// The menu lists each kind with its count and where it is handled, and a
// click goes there. Nothing is decided from the bell: approving somebody is
// the queue's job, with the queue's safeguards, and a shortcut that approved
// from a dropdown would be a second decision path to keep correct.
//
// Same menu idiom as `OrganizationSwitcher` and `NavMenu`: capture-phase
// outside click and Escape, registered only while open; not a `Dialog`.
// ═══════════════════════════════════════════════════════════════════

export interface PendingActionsBellProps {
  actions: PendingAction[];
  /** Go to the view where this kind is dealt with. */
  onGo: (view: View) => void;
}

export function PendingActionsBell({ actions, onGo }: PendingActionsBellProps) {
  const t = useT();
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement | null>(null);
  // The trigger, so closing the menu from the keyboard hands focus back to
  // what opened it rather than to <body> (WCAG 2.4.3, WAI-ARIA menu button;
  // `NavMenu` does the same, #1300).
  const triggerRef = useRef<HTMLButtonElement | null>(null);

  useEffect(() => {
    if (!open) return undefined;
    const clickOutside = (e: Event) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setOpen(false);
        triggerRef.current?.focus();
      }
    };
    document.addEventListener('mousedown', clickOutside, true);
    document.addEventListener('touchstart', clickOutside, true);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', clickOutside, true);
      document.removeEventListener('touchstart', clickOutside, true);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  if (actions.length === 0) return null;

  const total = actions.reduce((sum, a) => sum + a.count, 0);
  const waiting = actions.filter((a) => a.count > 0);

  const go = (view: View) => {
    setOpen(false);
    // The chosen item unmounts with the menu; without this, focus would fall
    // to <body>. In the portal `onGo` then moves focus on to the view's
    // heading, as it always did; this is the floor if a caller does not.
    triggerRef.current?.focus();
    onGo(view);
  };

  return (
    // Below `sm` the menu hangs from the header rather than from this button
    // (the wrapper is `static`): 18rem anchored at the button's right edge ran
    // off the left of a phone screen. From `sm` up it is unchanged.
    // `flex-shrink-0` (#1294): the header's right-hand group may shrink at
    // every width now, and a 34 px round button must not be what gives way.
    <div
      className="relative flex-shrink-0 max-sm:static"
      ref={containerRef}
      data-testid="header-pending-actions"
    >
      <button
        ref={triggerRef}
        type="button"
        onClick={() => setOpen((o) => !o)}
        className={`relative flex items-center justify-center rounded-full border transition-colors ${
          open ? 'bg-ink text-white border-ink' : 'bg-paper text-ink border-line hover:bg-bg-soft'
        }`}
        style={{ width: 34, height: 34 }}
        title={t('app.pendingActions')}
        aria-label={
          total > 0 ? t('app.pendingActions.waiting', { count: total }) : t('app.pendingActions')
        }
        aria-haspopup="menu"
        aria-expanded={open}
        data-testid="header-pending-actions-button"
      >
        <Bell size={16} aria-hidden="true" />
        {total > 0 && (
          <span
            className="absolute font-mono font-bold rounded-full flex items-center justify-center"
            style={{
              top: -5,
              right: -6,
              minWidth: 18,
              height: 18,
              padding: '0 5px',
              fontSize: 10,
              background: 'var(--lime)',
              color: 'var(--ink)',
              border: '2px solid var(--bg)',
            }}
            data-testid="header-pending-actions-badge"
          >
            {total > 99 ? '99+' : total}
          </span>
        )}
      </button>

      {open && (
        <div
          role="menu"
          aria-label={t('app.pendingActions')}
          className="absolute top-full right-0 mt-1 w-72 card-elev py-1.5 px-1.5 max-sm:left-3 max-sm:right-3 max-sm:w-auto"
          style={{ zIndex: 50 }}
          data-testid="header-pending-actions-menu"
        >
          <div className="px-2 py-1 font-mono text-[10px] uppercase tracking-wider text-ink-3">
            {t('app.pendingActions.heading')}
          </div>
          {waiting.length === 0 ? (
            <div className="px-2 py-1.5 text-sm text-ink-3">{t('app.pendingActions.none')}</div>
          ) : (
            waiting.map((action) => (
              <button
                key={action.kind}
                type="button"
                role="menuitem"
                className="w-full flex items-center gap-2 rounded px-2 py-1.5 text-left hover:bg-bg-soft"
                onClick={() => go(action.view)}
                data-testid={`pending-action-${action.kind}`}
              >
                <span className="min-w-0 flex-1">
                  <span className="block text-sm text-ink">{kindLabel(action, t)}</span>
                  <span className="block text-[11px] text-ink-3">
                    {destinationLabel(action, t)}
                  </span>
                </span>
                <ChevronRight size={14} className="text-ink-3" aria-hidden="true" />
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
}

/** «3 søknader om medlemskap» — one sentence per kind, singular and plural. */
function kindLabel(action: PendingAction, t: ReturnType<typeof useT>): string {
  switch (action.kind) {
    case 'join_requests':
      return action.count === 1
        ? t('app.pendingActions.joinRequests.one', { count: action.count })
        : t('app.pendingActions.joinRequests.other', { count: action.count });
  }
}

/** Where it is handled — the view's own name, so the menu and the nav agree. */
function destinationLabel(action: PendingAction, t: ReturnType<typeof useT>): string {
  switch (action.kind) {
    case 'join_requests':
      return t('app.pendingActions.goTo', { destination: t('ui.nav.item.players') });
  }
}
